/* =============================================================================
   DAILE Mono · content pages (mn-con-* sections). No dependencies; mn.js does reveal, rails and autoplay video.

   1. Table of contents: open on desktop (sticky column), collapsible on the phone (closes after a jump),
      the chapter on screen is marked with aria-current.
   2. FAQ: a link to #question opens that question; category chips mark the group on screen.
   3. Journal: copy link (with a short "Link copied" state) and the system share sheet where supported;
      the current topic chip is scrolled into view in the one-line topic row on the phone.
   4. Theme editor re-init.

   Each section renders this file (snippets/mn-con-assets.liquid) — window.mnCon makes it run once.
   ============================================================================= */
(() => {
  'use strict';

  if (window.mnCon) return;
  window.mnCon = true;

  const d = document;
  const hasIO = 'IntersectionObserver' in window;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const wide = window.matchMedia('(min-width: 990px)');

  const once = (el, key) => {
    if (el.hasAttribute(`data-mn-con-ready-${key}`)) return false;
    el.setAttribute(`data-mn-con-ready-${key}`, "");
    return true;
  };

  // Marks the link whose target is the last one above the line under the header.
  const spy = (links, onChange) => {
    const targets = links
      .map((a) => {
        const id = decodeURIComponent((a.getAttribute('href') || '').slice(1));
        return id ? d.getElementById(id) : null;
      });
    if (!hasIO || targets.every((t) => !t)) return;
    const pick = () => {
      let current = -1;
      const line = (parseFloat(getComputedStyle(d.documentElement).scrollPaddingTop) || 80) + 40;
      targets.forEach((t, i) => {
        if (t && t.getBoundingClientRect().top <= line) current = i;
      });
      links.forEach((a, i) => {
        if (i === current) a.setAttribute('aria-current', 'true');
        else a.removeAttribute('aria-current');
      });
      if (onChange) onChange(links[current] || null);
    };
    const io = new IntersectionObserver(() => pick(), { rootMargin: '0px 0px -55% 0px', threshold: [0, 1] });
    targets.forEach((t) => { if (t) io.observe(t); });
    let raf = 0;
    window.addEventListener('scroll', () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => { raf = 0; pick(); });
    }, { passive: true });
    pick();
  };

  /* 1. Table of contents ===================================================== */

  const initToc = (root) => {
    root.querySelectorAll('[data-mn-con-toc]').forEach((nav) => {
      if (!once(nav, 'toc')) return;
      const box = nav.querySelector('[data-mn-con-toc-box]');
      const links = Array.from(nav.querySelectorAll('[data-mn-con-toc-link]'));
      if (box && wide.matches) box.open = true;
      if (box && wide.addEventListener) wide.addEventListener('change', () => { box.open = wide.matches; });
      links.forEach((a) => a.addEventListener('click', () => { if (box && !wide.matches) box.open = false; }));
      spy(links);
    });
  };

  /* 2. FAQ =================================================================== */

  const openFromHash = () => {
    const id = decodeURIComponent(window.location.hash.slice(1));
    if (!id) return;
    const el = d.getElementById(id);
    if (el && el.tagName === 'DETAILS') {
      el.open = true;
      el.scrollIntoView({ block: 'start', behavior: reduce.matches ? 'auto' : 'smooth' });
    }
  };

  const initFaq = (root) => {
    root.querySelectorAll('[data-mn-con-faq]').forEach((faq) => {
      if (!once(faq, 'faq')) return;
      const chips = Array.from(faq.querySelectorAll('[data-mn-con-faq-chip]'));
      if (chips.length) {
        const row = chips[0].closest('ul');
        spy(chips, (a) => {
          if (!row || row.scrollWidth <= row.clientWidth) return;
          if (!a) {
            if (row.scrollLeft !== 0) row.scrollTo({ left: 0, behavior: 'smooth' });
            return;
          }
          {
            const r = a.getBoundingClientRect();
            const rr = row.getBoundingClientRect();
            if (r.left < rr.left || r.right > rr.right) row.scrollBy({ left: r.left - rr.left - 16, behavior: 'smooth' });
          }
        });
      }
    });
    openFromHash();
  };
  window.addEventListener('hashchange', openFromHash);

  /* 3. Journal ================================================================ */

  const initJournal = (root) => {
    root.querySelectorAll('[data-mn-con-copy]').forEach((btn) => {
      if (!once(btn, 'copy')) return;
      const label = btn.querySelector('[data-mn-con-copy-label]');
      const base = label ? label.textContent : '';
      let timer = 0;
      btn.addEventListener('click', async () => {
        const url = btn.getAttribute('data-mn-con-copy');
        let ok = false;
        try {
          await navigator.clipboard.writeText(url);
          ok = true;
        } catch (e) {
          const field = d.createElement('textarea');
          field.value = url;
          field.setAttribute('readonly', '');
          field.style.cssText = 'position:fixed;opacity:0;';
          d.body.appendChild(field);
          field.select();
          try { ok = d.execCommand('copy'); } catch (err) { ok = false; }
          field.remove();
        }
        if (ok && label) {
          label.textContent = btn.getAttribute('data-mn-con-copied') || base;
          btn.classList.add('is-done');
          window.clearTimeout(timer);
          timer = window.setTimeout(() => { label.textContent = base; btn.classList.remove('is-done'); }, 2200);
        }
      });
    });

    root.querySelectorAll('[data-mn-con-share]').forEach((btn) => {
      if (!once(btn, 'share')) return;
      if (typeof navigator.share !== 'function') return;
      const item = btn.closest('[hidden]');
      if (item) item.hidden = false;
      btn.addEventListener('click', () => {
        navigator.share({ title: btn.getAttribute('data-mn-con-title') || d.title, url: btn.getAttribute('data-mn-con-share') }).catch(() => {});
      });
    });

    root.querySelectorAll('[data-mn-con-topics]').forEach((row) => {
      if (!once(row, 'topics')) return;
      const current = row.querySelector('[aria-current]');
      if (current && row.scrollWidth > row.clientWidth) {
        const r = current.getBoundingClientRect();
        const rr = row.getBoundingClientRect();
        if (r.right > rr.right || r.left < rr.left) row.scrollLeft += r.left - rr.left - 16;
      }
    });
  };

  /* Init ===================================================================== */

  const init = (root) => {
    initToc(root);
    initFaq(root);
    initJournal(root);
  };

  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', () => init(d));
  else init(d);

  d.addEventListener('shopify:section:load', (e) => init(e.target));
  d.addEventListener('shopify:block:select', (e) => {
    const el = e.target;
    if (el && el.tagName === 'DETAILS') el.open = true;
  });
})();
